import { memo, useEffect, useState } from 'react'
import { Avatar, Box, Grid, Radio } from '@mui/material'
import Section from 'components/common/Section/Section'
import CustomText from 'components/common/CustomText/CustomText'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { useSetProductAvatarMedia } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'
import { IPhotoSize } from 'types/types'

interface Props {
  shared: any;
}

// The avatar is picked from the photos the product already has, the way the
// front photo is picked on a raw product's review card. Uploading a separate
// picture was the other way to answer this, and it is how a product ends up
// carrying the same photo twice - once in its strip and once as its front.
function ProductAvatarDetails({ shared }: Props) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const {
    setCarouselImages,
    setCarouselIndex,
    setCarouselOpen,
  } = shared;

  const images: IPhotoSize[] = product.mediaFiles ?? [];

  useEffect(() => {
    setSelectedId(product.avatarMediaId ?? null);
  }, [product.avatarMediaId]);

  const { mutate: setAvatarMedia, isLoading } = useSetProductAvatarMedia(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product avatar has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not update the product avatar', 'error');
    },
  )

  const handleSave = () => {
    // Nothing was moved, so there is nothing to send.
    if (!selectedId || selectedId === product.avatarMediaId) {
      setIsEditing(false);
      return;
    }

    setAvatarMedia({ productId: product.id, mediaId: selectedId });
  }

  const handleCancel = () => {
    setSelectedId(product.avatarMediaId ?? null);
    setIsEditing(false);
  }

  const handleTileClick = (image: IPhotoSize, index: number) => {
    if (isEditing) {
      setSelectedId(image.id ?? null);
      return;
    }

    setCarouselImages(images);
    setCarouselIndex(index);
    setCarouselOpen(true);
  }

  return (
    <Box mt={3}>
      <Section
        title="Product Avatar"
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => setIsEditing(true),
                disabled: isLoading || images.length === 0,
              }
            ]),
        ]}
        content={
          <Grid size={{ xs: 12 }}>
            {images.length === 0 ? (
              <CustomText
                text={'No photos yet - add some in Product Images, then pick one here.'}
                fontSize={14}
                color={colors.gray}
              />
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {images.map((image, index) => {
                  const isSelected = !!image.id && image.id === selectedId;

                  return (
                    <Box
                      key={image.id ?? index}
                      onClick={() => handleTileClick(image, index)}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        cursor: 'pointer',
                        // The same light blue the display-image picker marks a
                        // chosen photo with, so the two strips read alike.
                        border: `3px solid ${isSelected ? colors.edit : 'transparent'}`,
                      }}
                    >
                      <Avatar
                        src={image.medium || image.large || ''}
                        variant="rounded"
                        sx={{ width: 100, height: 100 }}
                      />

                      {/* Only while the pick is being made. Outside it the
                          border already says which photo is the front, and a
                          row of dead radios reads as something to click. */}
                      {isEditing && (
                      <Radio
                        size="small"
                        checked={isSelected}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedId(image.id ?? null);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          padding: 0.25,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                        }}
                      />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Grid>
        }
      />
    </Box>
  )
}

export default memo(ProductAvatarDetails)
